import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Accordion, AccordionItem, Link } from '@lyse-as/formds-react';
import { OpenInNewIcon } from '@lyse-as/formds-react/lib/icon/common';
import { ContainerFixed } from 'app/components/container';
import { ContainerLayoutMode } from 'app/components/container/container';
import { ActiveCustomerLocationPage } from 'app/pages/page-wrapper';
import { Breakpoint, Typography as T } from 'app/component-library-wave';
import { useLoadNetworkSettings } from 'app/hooks/use-network';
import { Service } from 'app/service/navigation/pages/page-types';
import { navigationService } from 'app/service/navigation/navigation-service';
import { Badge } from 'app/components/badge/badge';
import { Card } from 'app/components/card';
import { AltiboxChatBubbles } from 'app/media';
import { useAppDispatch } from 'app/hooks/redux-thunk';
import { getInsuranceDocument } from 'app/store/actions/insurance-thunks';
import styles from '../security.module.scss';

export const SecurityInsurance: ActiveCustomerLocationPage = (props) => {
  const { t } = useTranslation();
  const { customerCrmId: customerId, servicePointId, services } = props.userContext;
  const dispatch = useAppDispatch();

  const TryggConditions = [Service.TryggFSecure, Service.TryggForsikring];
  const [hasTrygg] = useState(services.some((service) => TryggConditions.includes(service)));

  useLoadNetworkSettings({ servicePointId, customerId });
  const includedServices = t('pages.security.insurance.included', { returnObjects: true }) as string[];
  const howItWorksBulletPoint = t('pages.security.insurance.howDoesItWork.bulletPoints', {
    returnObjects: true,
  }) as string[];
  const gettingStartedSteps = t('pages.security.insurance.howToGetStarted.steps', {
    returnObjects: true,
  }) as string[];

  useEffect(() => {
    if (hasTrygg === false) {
      window.location.href = navigationService.PAGES.internetSecurity.url;
    }
  }, [hasTrygg]);

  const getDocument = () => {
    dispatch(getInsuranceDocument(customerId));
  };

  return (
    <ContainerFixed mode={ContainerLayoutMode.Medium} className={styles.pageContainer}>
      <section>
        <div className={styles.headingWithBadge}>
          <T
            variant="formds-altibox-subtitle-primary"
            component="h1"
            maxBreakpoint={Breakpoint.TABLET}
            bold={true}
            className={styles.pageHeading}
          >
            {t('pages.security.insurance.name')}
          </T>
          <Badge className={styles.activatedBadge}>{t('pages.security.insurance.activated')}</Badge>
        </div>
        <T
          variant="formds-body"
          component="h2"
          maxBreakpoint={Breakpoint.TABLET}
          bold={true}
          className={styles.heading}
        >
          {t('pages.security.insurance.subHeading')}
        </T>
        <T variant="formds-body" component="p" maxBreakpoint={Breakpoint.TABLET}>
          {t('pages.security.insurance.paragraph1')}
        </T>
      </section>
      <section>
        <Accordion type="single" collapsible>
          <AccordionItem
            value="included"
            label={t('pages.security.insurance.includedHeading')}
            panel={
              <>
                <ul className={styles.bulletList}>
                  {includedServices.map((serviceDescription) => (
                    <T
                      key={serviceDescription}
                      variant="formds-body"
                      component="li"
                      maxBreakpoint={Breakpoint.TABLET}
                      className={styles.bulletListItem}
                    >
                      {serviceDescription}
                    </T>
                  ))}
                </ul>
                <T
                  variant="formds-altibox-subtitle-secondary"
                  component="p"
                  maxBreakpoint={Breakpoint.TABLET}
                  className={styles.paragraph}
                >
                  <Link href={`${window.origin}/trygg`}>{t('pages.security.insurance.readMoreLink')}</Link>
                </T>
              </>
            }
          />
          <AccordionItem
            value="how-it-works"
            label={t('pages.security.insurance.howDoesItWork.heading')}
            panel={
              <>
                <ul className={styles.bulletList}>
                  {howItWorksBulletPoint.map((bulletPoint) => (
                    <T
                      key={bulletPoint}
                      variant="formds-body"
                      component="li"
                      maxBreakpoint={Breakpoint.TABLET}
                      className={styles.bulletListItem}
                    >
                      <Trans
                        components={{
                          bold: <strong />,
                        }}
                      >
                        {bulletPoint}
                      </Trans>
                    </T>
                  ))}
                </ul>
                <T variant="formds-body" component="div">
                  <Trans i18nKey="pages.security.insurance.howDoesItWork.linkFaq">
                    placeholder <Link href="https://www.altibox.no/trygghjelp">placeholder</Link>
                  </Trans>
                </T>
              </>
            }
          />
          <AccordionItem
            value="getting-started"
            label={t('pages.security.insurance.howToGetStarted.heading')}
            panel={
              <ol className={styles.bulletList}>
                {gettingStartedSteps.map((step) => (
                  <T
                    key={step}
                    variant="formds-body"
                    component="li"
                    maxBreakpoint={Breakpoint.TABLET}
                    className={styles.orderedListItem}
                  >
                    {step}
                  </T>
                ))}
              </ol>
            }
          />
        </Accordion>
      </section>
      <section>
        <Card className={styles.teaserCardContainer} containerClassName={styles.teaserContainer}>
          <div className={styles.teaserContainer}>
            <div className={styles.teaserInformation}>
              <T
                variant="formds-common-subtitle-secondary"
                component="h2"
                maxBreakpoint={Breakpoint.TABLET}
                bold={true}
              >
                {t('pages.security.insurance.information.heading')}
              </T>
              <T
                variant="formds-body"
                component="p"
                maxBreakpoint={Breakpoint.MOBILE}
                className={styles.contactDetails}
              >
                <Trans
                  i18nKey="pages.security.insurance.information.paragraph1"
                  values={{
                    phone: '22 99 99 99',
                    email: 'post@help.no',
                  }}
                >
                  Placeholder <Link href="tel:+4722999999">placeholder</Link>
                  <Link href="mailto:post@help.no">placeholder</Link>
                </Trans>
              </T>
              <Link href={'#'} onClick={getDocument} trailingIcon={<OpenInNewIcon />}>
                {t('pages.security.insurance.information.linkDocuments')}
              </Link>
              <Link
                href={`https://kundeservice.altibox.no/hc/no/articles/12797790458129-Vilk%C3%A5r-Altibox-privat`}
                trailingIcon={<OpenInNewIcon />}
              >
                {t('pages.security.insurance.information.linkTerms')}
              </Link>
            </div>
            <div>
              <AltiboxChatBubbles width={80} height={78} />
            </div>
          </div>
        </Card>
      </section>
    </ContainerFixed>
  );
};
